import { z } from 'zod';

/** Catálogo cerrado de `type` para problem+json (RFC 9457). El prefijo es estable y no resuelve a nada. */
export const PROBLEM_BASE = 'https://tasks/errors/' as const;

export const PROBLEM_TYPES = [
  'validation',
  'not-found',
  'conflict',
  'forbidden',
  'unauthorized',
  'token-expired',
  'token-invalid',
  'token-reuse',
  'session-expired',
  'rate-limited',
  'internal',
] as const;
export type ProblemType = (typeof PROBLEM_TYPES)[number];

export const problemTypeUrl = (type: ProblemType): string => `${PROBLEM_BASE}${type}`;

/**
 * Objeto laxo a propósito: RFC 9457 §3.2 permite miembros de extensión y el servidor manda
 * `current` en el 409 de versión. Un `z.object` estricto los descartaría al parsear y la UI
 * no podría rebasear sin recargar (diseño §7).
 */
export const problemDetailsSchema = z.looseObject({
  type: z.string().url(),
  title: z.string(),
  status: z.number().int(),
  detail: z.string().optional(),
  instance: z.string().optional(),
  errors: z.array(z.object({ path: z.string(), message: z.string() })).optional(),
});
export type ProblemDetails = z.infer<typeof problemDetailsSchema>;

/** UUID v7: ordenable por tiempo; lo genera el cliente en las creaciones (idempotencia por PK). */
export const idSchema = z.string().uuid();
export type Id = z.infer<typeof idSchema>;

/** `viewer`: solo lectura (la sesión del puente de Vitto con `can_write = false`); `PolicyGuard` le niega todo método que escribe. */
export const USER_ROLES = ['admin', 'member', 'viewer'] as const;
export type UserRole = (typeof USER_ROLES)[number];

/** De dónde nace una sesión: `local` (login/setup) o `vitto` (canje de un pase en `POST /auth/vitto`, con tope absoluto corto). */
export const SESSION_ORIGINS = ['local', 'vitto'] as const;
export type SessionOrigin = (typeof SESSION_ORIGINS)[number];

export const EVENT_ENTITY_TYPES = [
  'work_item',
  'comment',
  'attachment',
  'project',
  'label',
  'user',
] as const;
export type EventEntityType = (typeof EVENT_ENTITY_TYPES)[number];

/** Cuerpo del handle sin anclas: lo reutiliza la regex de menciones de `markdown` (diseño §3.1). */
export const HANDLE_BODY = String.raw`[a-z0-9][a-z0-9_-]{1,31}`;
export const HANDLE_PATTERN = new RegExp(`^${HANDLE_BODY}$`);
export const handleSchema = z.string().regex(HANDLE_PATTERN, 'invalid handle');
